import { Button, Form, Input, Modal, Popconfirm, Radio, Space, Table, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import {
  deleteContractTemplate,
  errorText,
  listContractTemplates,
  saveContractTemplate,
} from '~/services/teacher-contract'
import { ITeacherContractTemplate } from '~/types/teacher-contract'

import { LANGUAGE_LABEL, MARKUP_HELP, fmt } from './constants'

/** 合同模板：改模板只影响之后新发的合同，已发出的合同各自保存了当时的正文和 PDF */
const TemplatePanel = () => {
  const [items, setItems] = useState<ITeacherContractTemplate[]>([])
  const [builtIns, setBuiltIns] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<ITeacherContractTemplate | 'new' | null>(null)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const load = useCallback(() => {
    setLoading(true)
    listContractTemplates()
      .then((res) => {
        setItems(res.data.items)
        setBuiltIns(res.data.builtInPlaceholders)
      })
      .catch(async (err) => message.error(await errorText(err, '加载失败')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (editing === 'new') {
      form.resetFields()
      form.setFieldValue('language', 'EN')
    }
    else if (editing) form.setFieldsValue(editing)
  }, [editing, form])

  const onSave = async () => {
    const v = await form.validateFields()
    setSaving(true)
    try {
      await saveContractTemplate(v, editing && editing !== 'new' ? editing.id : undefined)
      message.success('已保存')
      setEditing(null)
      load()
    } catch (err) {
      message.error(await errorText(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Button type="primary" style={{ marginBottom: 16 }} onClick={() => setEditing('new')}>
        新建模板
      </Button>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        pagination={false}
        columns={[
          { title: '模板名称', dataIndex: 'name' },
          {
            title: '语言',
            dataIndex: 'language',
            width: 80,
            render: (v: ITeacherContractTemplate['language']) => <Tag>{LANGUAGE_LABEL[v]}</Tag>,
          },
          { title: '合同标题', dataIndex: 'title' },
          {
            title: '变量',
            dataIndex: 'placeholders',
            render: (ps: string[]) =>
              ps.map((p) => (
                <Tag key={p} color={builtIns.includes(p) ? 'default' : 'blue'}>
                  {p}
                </Tag>
              )),
          },
          { title: '更新时间', dataIndex: 'updatedAt', width: 150, render: fmt },
          {
            title: '操作',
            key: 'actions',
            width: 120,
            render: (_, r) => (
              <Space>
                <a onClick={() => setEditing(r)}>编辑</a>
                <Popconfirm
                  title="删除这个模板？已发出的合同不受影响"
                  onConfirm={async () => {
                    try {
                      await deleteContractTemplate(r.id)
                      load()
                    } catch (err) {
                      message.error(await errorText(err, '删除失败'))
                    }
                  }}
                >
                  <a style={{ color: '#ff4d4f' }}>删除</a>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        open={editing != null}
        title={editing === 'new' ? '新建模板' : '编辑模板'}
        width={900}
        onCancel={() => setEditing(null)}
        onOk={onSave}
        confirmLoading={saving}
        okText="保存"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="模板名称（内部用）" rules={[{ required: true }]}>
            <Input maxLength={128} placeholder="例如：外教业务委托合同 v1" />
          </Form.Item>
          <Form.Item
            name="language"
            label="合同语言"
            extra="正文按这个语言写；签署页、审计页、页脚和发给老师的邮件也会用这个语言。英文老师、日文老师各建一份模板。"
            rules={[{ required: true }]}
          >
            <Radio.Group
              options={[
                { value: 'EN', label: LANGUAGE_LABEL.EN },
                { value: 'JA', label: LANGUAGE_LABEL.JA },
              ]}
            />
          </Form.Item>
          <Form.Item name="title" label="合同标题（发给老师看到的）" rules={[{ required: true }]}>
            <Input maxLength={255} placeholder="Independent Contractor Agreement" />
          </Form.Item>
          <Form.Item
            name="body"
            label="合同正文"
            rules={[{ required: true }]}
            extra={
              <div style={{ marginTop: 8 }}>
                {MARKUP_HELP.map((line) => (
                  <div key={line}>{line}</div>
                ))}
                <div style={{ marginTop: 4 }}>
                  系统自动填的变量：
                  {builtIns.map((p) => (
                    <Typography.Text key={p} code>{`{{${p}}}`}</Typography.Text>
                  ))}
                </div>
                <div>签名不用写在正文里：老师签署后系统会在最后自动加「签署页」和「审计页」。</div>
              </div>
            }
          >
            <Input.TextArea autoSize={{ minRows: 16, maxRows: 30 }} style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default TemplatePanel
